import React, { useMemo, useState } from 'react';

import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { type FormattedTaskOption } from 'src/utils/restrictionUtils';
import { createSearchRegExp } from 'src/utils/searchUtils';

// **** Types **** //

interface AllTasksSelectorProps {
  availableOptions: FormattedTaskOption[];
  selectedOptions: string[];
  onSelectTasks: (entityIds: string[]) => void;
  isDisabled: boolean;
}

// **** Components **** //

/**
 * Select a "RestrictionItem" from a dropdown list.
 */
const AllTasksSelector = ({ availableOptions, selectedOptions, onSelectTasks, isDisabled }: AllTasksSelectorProps) => {
  const [tasksQuery, setTasksQuery] = useState<string>();

  // Apply user entered filter string
  const matchedTaskOptions = useMemo(() => {
    if (!tasksQuery) {
      return availableOptions;
    }
    const taskSearchRegExp = createSearchRegExp(tasksQuery);
    return availableOptions.filter((option) => !tasksQuery || taskSearchRegExp.test(option.label));
  }, [availableOptions, tasksQuery]);

  return (
    <Select
      data-cy="new-jmr__restrictions__all-tasks__dropdown-selector"
      data-testid="new-jmr__restrictions__all-tasks__dropdown-selector"
      searchQuery={tasksQuery}
      onSearch={(v) => setTasksQuery(v)}
      placeholder="Select task"
      value={selectedOptions}
      onChange={onSelectTasks}
      disabled={isDisabled}
    >
      {matchedTaskOptions.map((option) => (
        <SelectOption
          key={option.key}
          label={option.label}
          value={option.value}
          data-cy="new-jmr__restrictions__all-tasks__dropdown-item"
        />
      ))}
      {!matchedTaskOptions.length && (
        <Column alignmentVertical="center" spacing="300" spacingInset="500">
          <Text alignment="center">No tasks found</Text>
        </Column>
      )}
    </Select>
  );
};

// **** Export **** //

export default AllTasksSelector;
